
import React, { useState } from 'react';
import Button from '../../../components/base/Button';

const FinalCTASection: React.FC = () => {
  const [showLaunchForm, setShowLaunchForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    industry: '',
    current_challenges: '',
    budget_range: '',
    timeline: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const formDataToSend = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        formDataToSend.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d42esc9ha0trr1ha99i0', {
        method: 'POST',
        body: formDataToSend,
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ 
          name: '', 
          email: '', 
          company: '', 
          industry: '', 
          current_challenges: '', 
          budget_range: '', 
          timeline: '' 
        });
        setTimeout(() => {
          setShowLaunchForm(false);
          setSubmitStatus('idle');
        }, 2000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="py-32 bg-black relative overflow-hidden">
      <div className="max-w-5xl mx-auto px-6 text-center relative z-10">
        <h3 className="text-5xl md:text-7xl font-light text-white mb-8 leading-tight tracking-tight">
          Ready to get
          <br />
          <span className="font-medium">started?</span>
        </h3>
        
        <p className="text-xl md:text-2xl text-gray-300 mb-12 max-w-3xl mx-auto leading-relaxed font-light">
          Transform your business with intelligent automation today.
        </p>
        
        <Button 
          variant="secondary" 
          size="lg"
          onClick={() => setShowLaunchForm(true)}
          className="text-lg px-12 py-4"
        >
          Get Started
        </Button>
      </div>

      {/* Launch Form Modal */}
      {showLaunchForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm">
          <div className="bg-white rounded-3xl p-8 max-w-lg w-full mx-4 shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-8">
              <h4 className="text-2xl font-medium text-black">Get Started</h4>
              <button
                onClick={() => setShowLaunchForm(false)}
                className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-black cursor-pointer rounded-full hover:bg-gray-100 transition-colors"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            
            <form onSubmit={handleSubmit} data-readdy-form id="ai-system-launch-form">
              <div className="space-y-4">
                <div>
                  <input
                    type="text"
                    name="name"
                    placeholder="Full Name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-4 border border-gray-200 rounded-2xl focus:outline-none focus:border-black transition-colors text-base bg-gray-50 focus:bg-white"
                  />
                </div>
                <div>
                  <input
                    type="email"
                    name="email"
                    placeholder="Email Address"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-4 border border-gray-200 rounded-2xl focus:outline-none focus:border-black transition-colors text-base bg-gray-50 focus:bg-white"
                  />
                </div>
                <div>
                  <input
                    type="text"
                    name="company"
                    placeholder="Company Name"
                    value={formData.company}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-4 border border-gray-200 rounded-2xl focus:outline-none focus:border-black transition-colors text-base bg-gray-50 focus:bg-white"
                  />
                </div>
                <div>
                  <select
                    name="industry"
                    value={formData.industry}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-4 border border-gray-200 rounded-2xl focus:outline-none focus:border-black transition-colors text-base pr-8 bg-gray-50 focus:bg-white"
                  >
                    <option value="">Select Industry</option>
                    <option value="healthcare">Healthcare</option>
                    <option value="professional-services">Professional Services</option>
                    <option value="financial-services">Financial Services</option>
                    <option value="home-services">Home Services</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <textarea
                    name="current_challenges"
                    placeholder="What are your current business challenges?"
                    value={formData.current_challenges}
                    onChange={handleInputChange}
                    maxLength={500}
                    rows={3}
                    className="w-full px-4 py-4 border border-gray-200 rounded-2xl focus:outline-none focus:border-black transition-colors text-base resize-none bg-gray-50 focus:bg-white"
                  ></textarea>
                  <div className="text-xs text-gray-400 mt-2">
                    {formData.current_challenges.length}/500 characters
                  </div>
                </div>
                <div>
                  <select
                    name="budget_range"
                    value={formData.budget_range}
                    onChange={handleInputChange}
                    className="w-full px-4 py-4 border border-gray-200 rounded-2xl focus:outline-none focus:border-black transition-colors text-base pr-8 bg-gray-50 focus:bg-white"
                  >
                    <option value="">Budget Range (Optional)</option>
                    <option value="under-5k">Under $5,000</option>
                    <option value="5k-15k">$5,000 - $15,000</option>
                    <option value="15k-30k">$15,000 - $30,000</option>
                    <option value="30k-plus">$30,000+</option>
                  </select>
                </div>
                <div>
                  <select
                    name="timeline"
                    value={formData.timeline}
                    onChange={handleInputChange}
                    className="w-full px-4 py-4 border border-gray-200 rounded-2xl focus:outline-none focus:border-black transition-colors text-base pr-8 bg-gray-50 focus:bg-white"
                  >
                    <option value="">Timeline (Optional)</option>
                    <option value="asap">ASAP</option>
                    <option value="1-month">Within 1 month</option>
                    <option value="2-3-months">2-3 months</option>
                    <option value="3-plus-months">3+ months</option>
                  </select>
                </div>
              </div>
              
              <div className="mt-8">
                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  disabled={isSubmitting}
                  className="w-full text-base"
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Request'}
                </Button>
              </div>
              
              {submitStatus === 'success' && (
                <div className="mt-4 p-4 bg-green-50 text-green-800 rounded-2xl text-sm text-center">
                  Request submitted successfully! We'll contact you within 24 hours.
                </div>
              )}
              
              {submitStatus === 'error' && (
                <div className="mt-4 p-4 bg-red-50 text-red-800 rounded-2xl text-sm text-center">
                  Something went wrong. Please try again.
                </div>
              )}
            </form>
          </div>
        </div>
      )}
    </section>
  );
};

export default FinalCTASection;
